import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Card,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  FormControl,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  ExitToApp as LogoutIcon,
  Add as AddIcon,
  Backup as BackupIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  getClients,
  getWeekEntries,
  getWeekEntriesGrouped,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  getLastEndTime,
  createClient,
  getAllTimeEntriesForBackup,
  getAllClientsForBackup,
  getEntriesByClientAndDateRange,
} from './api';
import {
  generateTimeOptions,
  calculateDuration,
  formatDuration,
  formatDate,
  getStartOfWeek,
  parseEntryDate,
  isWithinWeek
} from './utils';

const DRAWER_WIDTH = 260;

const TimeTracking = ({ user, onLogout, onNavigate }) => {
  const [currentUser] = useState(user.username);
  const [description, setDescription] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClientName, setSelectedClientName] = useState('');
  const [entryDate, setEntryDate] = useState(formatDate(new Date()));
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [mileage, setMileage] = useState('');

  const [clients, setClients] = useState([]);
  const [weekEntries, setWeekEntries] = useState([]);
  const [weekTotal, setWeekTotal] = useState('0:00');
  const [weekOffset, setWeekOffset] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [newClientName, setNewClientName] = useState('');

  const [openBackupDialog, setOpenBackupDialog] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);

  const [openClientReportDialog, setOpenClientReportDialog] = useState(false);
  const [reportClientId, setReportClientId] = useState('');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportEntries, setReportEntries] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  const timeOptions = generateTimeOptions();


  // --- Group entries by normalized local date ---
  const groupEntriesByDate = (entries = []) => {
    const grouped = {};
  
    entries.forEach((entry) => {
      const parsed = parseEntryDate(entry);
      if (!parsed) return;
  
      // Use YYYY-MM-DD as key (local date)
      const localKey = parsed.toISOString().split('T')[0];
  
      if (!grouped[localKey]) {
        grouped[localKey] = {
          date: localKey,
          entries: [],
          total: 0,
          sortDate: localKey,
        };
      }
  
      grouped[localKey].entries.push(entry);
      grouped[localKey].total += parseFloat(entry.duration) || 0;
    });
  
    // Sort newest first
    return Object.values(grouped).sort((a, b) => b.sortDate.localeCompare(a.sortDate));
  };

  const getWeekStartWithOffset = () => {
    const today = new Date();
    const start = getStartOfWeek(today);
    start.setDate(start.getDate() + weekOffset * 7);
    return start;
  };

  const getWeekRangeDisplay = () => {
    const weekStart = getWeekStartWithOffset();
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const s = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const e = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} - ${e}`;
  };

  useEffect(() => {
    loadClients();
    loadWeekEntriesGrouped();
    loadWeekEntries();
    loadLastEndTime();
  }, []);

  useEffect(() => {
    loadWeekEntriesGrouped();
    loadWeekEntries();
  }, [weekOffset]);

  /** === API Calls === */
  const loadClients = async () => {
    try {
      const data = await getClients();
      if (data.items) {
        const sortedClients = data.items
          .map((i) => ({ id: i.clientid_pk, name: i.companyname }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setClients(sortedClients);
      }
    } catch (err) {
      setError('Failed to load clients: ' + err.message);
    }
  };

const loadWeekEntries = useCallback(async () => {
  try {
    const weekStart = getWeekStartWithOffset();
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const data = await getWeekEntries(currentUser, formatDate(weekStart));

    const totalReturned = Array.isArray(data?.items) ? data.items.length : 0;
    console.debug('[loadWeekEntries] fetched total items:', totalReturned);

    if (totalReturned > 0) {
      // sample the first 3 items for inspection
      console.debug('[loadWeekEntries] sample items:', data.items.slice(0, 3));

      const weekEntries = data.items.filter((entry) => {
        const d = parseEntryDate(entry);
        if (!d || isNaN(d.getTime())) return false;
        return d >= weekStart && d <= weekEnd;
      });

      console.debug('[loadWeekEntries] filtered weekEntries count:', weekEntries.length);

      const total = weekEntries.reduce((sum, entry) => sum + (parseFloat(entry.duration) || 0), 0);
      setWeekTotal(formatDuration(total));
    } else {
      setWeekTotal('0:00');
    }
  } catch (err) {
    console.error('Failed to load week total:', err);
    setWeekTotal('0:00');
  }
}, [currentUser, weekOffset]);


const loadWeekEntriesGrouped = useCallback(async () => {
  try {
    const weekStart = getWeekStartWithOffset();
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const data = await getWeekEntriesGrouped(currentUser, formatDate(weekStart));

    const allItems = data?.items || [];
    console.debug('[loadWeekEntriesGrouped] fetched total items:', allItems.length);

    if (allItems.length > 0) {
      console.debug('[loadWeekEntriesGrouped] sample items:', allItems.slice(0, 3));

      // Filter entries by normalized local dates
      const weekEntries = allItems.filter((entry) => {
        const d = parseEntryDate(entry);
        if (!d || isNaN(d.getTime())) return false;
        return isWithinWeek(d, weekStart, weekEnd);
      });

      console.debug('[loadWeekEntriesGrouped] filtered weekEntries count:', weekEntries.length);
      setWeekEntries(weekEntries);
    } else {
      setWeekEntries([]);
    }
  } catch (err) {
    console.error('Failed to load week entries:', err);
    setWeekEntries([]);
  }
}, [currentUser, weekOffset]);


  const loadLastEndTime = async () => {
    try {
      const data = await getLastEndTime(currentUser, entryDate);
      const last = data.items?.[0];
      if (last?.end_time) setStartTime(last.end_time);
    } catch (err) {
      console.error('Failed to load last end time:', err);
    }
  };

  /** === CRUD === */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!description.trim()) return setError('Description is required');
    if (!selectedClientId) return setError('Please select a client');
    if (!startTime) return setError('Please select a start time');
    if (!endTime) return setError('Please select an end time');

    const duration = calculateDuration(startTime, endTime);
    if (duration <= 0) return setError('End time must be after start time');

    setLoading(true);
    try {
      const entryData = {
        clientid: parseInt(selectedClientId),
        client: selectedClientName,
        description,
        user_: currentUser,
        billable: isBillable ? 'Y' : 'N',
        start_date: entryDate + 'T00:00:00Z',
        start_time: startTime,
        end_time: endTime,
        duration,
        mileage: parseFloat(mileage) || 0,
      };

      if (isEditMode && editingEntry) {
        await updateTimeEntry(editingEntry.timedata_id_pk, entryData);
        setSuccess('Time entry updated successfully!');
        setIsEditMode(false);
        setEditingEntry(null);
      } else {
        await createTimeEntry(entryData);
        setSuccess('Time entry added successfully!');
      }

      setDescription('');
      setStartTime('');
      setEndTime('');
      setMileage('');
      await Promise.all([loadWeekEntriesGrouped(), loadWeekEntries(), loadLastEndTime()]);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding entry:', err);
      const msg = err.response?.data?.message || err.message;
      setError('Failed to add entry: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    setLoading(true);
    try {
      await deleteTimeEntry(id);
      setSuccess('Entry deleted successfully!');
      await Promise.all([loadWeekEntriesGrouped(), loadWeekEntries()]);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete entry: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    const d = parseEntryDate(entry);
    const formatted = d ? d.toISOString().split('T')[0] : formatDate(new Date());
    setEditingEntry(entry);
    setIsEditMode(true);
    setDescription(entry.description);
    setSelectedClientId(entry.clientid);
    setSelectedClientName(entry.client);
    setEntryDate(formatted);
    setStartTime(entry.start_time);
    setEndTime(entry.end_time);
    setIsBillable(entry.billable === 'Y');
    setMileage(entry.mileage || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setIsEditMode(false);
    setDescription('');
    setStartTime('');
    setEndTime('');
    setMileage('');
  };

  const handleClientChange = (e) => {
    const id = e.target.value;
    setSelectedClientId(id);
    const client = clients.find((c) => c.id === id);
    if (client) setSelectedClientName(client.name);
  };

  const handleAddClient = async () => {
    if (!newClientName.trim()) {
      setError('Client name is required');
      return;
    }

    setLoading(true);
    try {
      await createClient({ companyname: newClientName.trim() });
      setSuccess('Client added successfully!');
      setNewClientName('');
      setOpenClientDialog(false);
      await loadClients();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding client:', err);
      const msg = err.response?.data?.message || err.message;
      setError('Failed to add client: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupData = async (format) => {
    setBackupLoading(true);
    setError('');

    try {
      // Fetch all data
      const [timeEntriesData, clientsData] = await Promise.all([
        getAllTimeEntriesForBackup(),
        getAllClientsForBackup()
      ]);

      const timeEntries = timeEntriesData.items || [];
      const clients = clientsData.items || [];

      if (format === 'json') {
        // Create JSON backup
        const backup = {
          exportDate: new Date().toISOString(),
          exportedBy: currentUser,
          data: {
            timeEntries: timeEntries,
            clients: clients,
            summary: {
              totalTimeEntries: timeEntries.length,
              totalClients: clients.length,
            }
          }
        };

        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sbas-timetracking-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setSuccess(`Backup created successfully! (${timeEntries.length} time entries, ${clients.length} clients)`);
      } else if (format === 'csv') {
        // Create CSV backup for time entries
        const csvRows = [];
        csvRows.push([
          'ID',
          'Client ID',
          'Client Name',
          'Description',
          'User',
          'Billable',
          'Start Date',
          'Start Time',
          'End Time',
          'Duration (hours)',
          'Mileage'
        ].join(','));

        timeEntries.forEach(entry => {
          csvRows.push([
            entry.timedata_id_pk,
            entry.clientid,
            `"${(entry.client || '').replace(/"/g, '""')}"`,
            `"${(entry.description || '').replace(/"/g, '""')}"`,
            entry.user_,
            entry.billable,
            entry.start_date,
            entry.start_time,
            entry.end_time,
            entry.duration,
            entry.mileage || 0
          ].join(','));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sbas-timetracking-backup-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setSuccess(`CSV backup created successfully! (${timeEntries.length} time entries)`);
      }

      setOpenBackupDialog(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating backup:', err);
      const msg = err.response?.data?.message || err.message;
      setError('Failed to create backup: ' + msg);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleGenerateClientReport = async () => {
    if (!reportClientId) {
      setError('Please select a client');
      return;
    }
    if (!reportStartDate || !reportEndDate) {
      setError('Please select both start and end dates');
      return;
    }

    setReportLoading(true);
    setError('');

    try {
      const data = await getEntriesByClientAndDateRange(reportClientId, reportStartDate, reportEndDate);
      setReportEntries(data.items || []);

      if (data.items.length === 0) {
        setError('No entries found for the selected client and date range');
      }
    } catch (err) {
      console.error('Error generating client report:', err);
      const msg = err.response?.data?.message || err.message;
      setError('Failed to generate report: ' + msg);
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportClientReport = () => {
    const client = clients.find(c => c.id === reportClientId);
    const clientName = client ? client.name : 'Unknown';

    const csvRows = [];
    csvRows.push([
      'Date',
      'User',
      'Description',
      'Start Time',
      'End Time',
      'Duration (hours)',
      'Billable',
      'Mileage'
    ].join(','));

    reportEntries.forEach(entry => {
      csvRows.push([
        entry.start_date,
        entry.user_,
        `"${(entry.description || '').replace(/"/g, '""')}"`,
        entry.start_time,
        entry.end_time,
        entry.duration,
        entry.billable,
        entry.mileage || 0
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${clientName}-report-${reportStartDate}-to-${reportEndDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccess('Report exported successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  // === UI ===
  return (
      <Box sx={{ display: 'flex' }}>
      
      {/* === Left Sidebar === */}
<Drawer

        variant="permanent"

        sx={{

          width: DRAWER_WIDTH,

          flexShrink: 0,

          '& .MuiDrawer-paper': {

            width: DRAWER_WIDTH,

            boxSizing: 'border-box',

            bgcolor: '#4a5d8f',

            color: 'white',

            borderRight: 'none',

            display: 'flex',

            flexDirection: 'column',

            minheight: '100vh',

            height: '100%',

            position: 'relative',

          },

        }}

      >

        {/* Logo/Brand */}
        <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <img
              src="/logo.png"
              alt="SBAS Logo"
              style={{
                width: '180px',
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
            Time Tracking
          </Typography>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            by SBAS
          </Typography>
        </Box>



        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2 }} />



        {/* === Navigation === */}

        <List sx={{px: 2, py: 3}}>

          <ListItem

            button

            selected

            sx={{

              borderRadius: 2,

              mb: 1,

              '&.Mui-selected': {

                bgcolor: 'rgba(255,255,255,0.15)',

              },

            }}

          >

            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>

              <ScheduleIcon />

            </ListItemIcon>

            <ListItemText

              primary="My Timesheets"

              primaryTypographyProps={{

                fontWeight: 500,

                fontSize: '0.95rem',

              }}

            />

          </ListItem>



          <ListItem

            button

            onClick={() => onNavigate('reports')}

            sx={{

              borderRadius: 2,

              mb: 1,

              '&:hover': {

                bgcolor: 'rgba(255,255,255,0.1)',

              },

            }}

          >

            <ListItemIcon

              sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}

            >

              <AssessmentIcon />

            </ListItemIcon>

            <ListItemText

              primary="Reports"

              primaryTypographyProps={{

                fontWeight: 400,

                fontSize: '0.95rem',

                color: 'rgba(255,255,255,0.85)',

              }}

            />

          </ListItem>

          <ListItem

            button

            onClick={() => setOpenClientReportDialog(true)}

            sx={{

              borderRadius: 2,

              mb: 1,

              '&:hover': {

                bgcolor: 'rgba(255,255,255,0.1)',

              },

            }}

          >

            <ListItemIcon

              sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}

            >

              <SearchIcon />

            </ListItemIcon>

            <ListItemText

              primary="Client Reports"

              primaryTypographyProps={{

                fontWeight: 400,

                fontSize: '0.95rem',

                color: 'rgba(255,255,255,0.85)',

              }}

            />

          </ListItem>

          {/* Employees menu - Admin only */}
          {user?.isAdmin && (
            <ListItem
              button
              onClick={() => {
                console.log('🔵 [Navigation] Employees button clicked');
                console.log('🔵 [Navigation] onNavigate function:', onNavigate);
                console.log('🔵 [Navigation] user.isAdmin:', user?.isAdmin);
                onNavigate && onNavigate('employees');
              }}
              sx={{
                borderRadius: 2,
                mb: 1,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}
              >
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Employees"
                primaryTypographyProps={{
                  fontWeight: 400,
                  fontSize: '0.95rem',
                  color: 'rgba(255,255,255,0.85)',
                }}
              />
            </ListItem>
          )}

          {/* Backup Data menu - Admin only */}
          {user?.isAdmin && (
            <ListItem
              button
              onClick={() => setOpenBackupDialog(true)}
              sx={{
                borderRadius: 2,
                mb: 1,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}
              >
                <BackupIcon />
              </ListItemIcon>
              <ListItemText
                primary="Backup Data"
                primaryTypographyProps={{
                  fontWeight: 400,
                  fontSize: '0.95rem',
                  color: 'rgba(255,255,255,0.85)',
                }}
              />
            </ListItem>
          )}

        </List>



        {/* === User Footer === */}

       <Box sx={{ mt: 'auto', p: 3, pb: 2 }}>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2 }} />

            <Stack direction="row" spacing={2} alignItems="center">

              <Avatar sx={{ width: 40, height: 40, bgcolor: '#5d7ab8' }}>

                {user.name.charAt(0)}

              </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>

              <Typography

                variant="body2"

                sx={{ fontWeight: 600, color: 'white', fontSize: '0.9rem' }}

              >

                {user.name}

              </Typography>

              <Typography

                variant="caption"

                sx={{ color: 'rgba(255,255,255,0.7)' }}

              >

                {user.role}

              </Typography>

            </Box>

            <IconButton

              size="small"

              onClick={onLogout}

              sx={{

                color: 'rgba(255,255,255,0.7)',

                '&:hover': { color: 'white' },

              }}

            >

              <LogoutIcon fontSize="small" />

            </IconButton>

          </Stack>

        </Box>

      </Drawer>

      {/* === Main Content === */}
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* === Alerts === */}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* === Week Header === */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Week of {getWeekRangeDisplay()}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setWeekOffset((o) => o - 1)}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => setWeekOffset(0)}
              >
                Current
              </Button>
              <Button
                variant="outlined"
                onClick={() => setWeekOffset((o) => o + 1)}
              >
                Next
              </Button>
            </Stack>
          </Stack>
          <Typography
              variant="h5"
              sx={{
                mt: 2,
                fontWeight: 700,
                color: '#005319ff',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <ScheduleIcon sx={{ fontSize: 20 }} />
              Total Hours: {weekTotal.replace(':', 'h ') + 'm'}
            </Typography>
                    </Card>

        {/* === Add/Edit Form === */}
{/* === Add/Edit Form === */}
    <Card sx={{ p: 3, mb: 4 }}>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {/* === Client Row === */}
          <FormControl fullWidth>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Client
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenClientDialog(true)}
                sx={{ textTransform: 'none' }}
              >
                Add Client
              </Button>
            </Stack>
            <Select
              value={selectedClientId}
              onChange={handleClientChange}
              displayEmpty
              required
              size="small"
            >
              <MenuItem value="">Select Client</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* === Description Row === */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Description
            </Typography>
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              size="small"
              fullWidth
              placeholder="Enter work description"
            />
          </Box>

          {/* === Details Row === */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="center"
          >
            {/* Date */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Date
              </Typography>
              <TextField
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                required
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Start */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Start
              </Typography>
              <Select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                displayEmpty
                required
                size="small"
                fullWidth
              >
                <MenuItem value="">Start Time</MenuItem>
                {timeOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </Box>

        {/* End */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            End
          </Typography>
          <Select
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            displayEmpty
            required
            size="small"
            fullWidth
          >
            <MenuItem value="">End Time</MenuItem>
            {timeOptions.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Mileage */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Mileage
          </Typography>
          <TextField
            type="number"
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            size="small"
            fullWidth
            placeholder="0"
          />
        </Box>

        {/* Billable Button */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Billable
          </Typography>
          <Button
            variant={isBillable ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            startIcon={<MoneyIcon />}
            onClick={() => setIsBillable((b) => !b)}
            sx={{
              whiteSpace: 'nowrap',
              minWidth: 120,
              textTransform: 'none',
            }}
          >
            {isBillable ? 'Yes' : 'No'}
          </Button>
        </Box>
      </Stack>

      {/* === Action Buttons === */}
      <Stack direction="row" spacing={2}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} /> : <ScheduleIcon />
          }
        >
          {isEditMode ? 'Update Entry' : 'Add Entry'}
        </Button>

        {isEditMode && (
          <Button variant="outlined" color="secondary" onClick={handleCancelEdit}>
            Cancel Edit
          </Button>
        )}
      </Stack>
    </Stack>
  </form>
</Card>

        {/* === Entries List === */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Weekly Entries
          </Typography>

          {weekEntries.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No entries found for this week.
            </Typography>
          )}

          {groupEntriesByDate(weekEntries).map((group) => {
            // derive a readable date label
            const firstEntry = group.entries[0];
            const parsedDate = parseEntryDate(firstEntry);
            const dateLabel = parsedDate
              ? parsedDate.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })
              : group.date;

            return (
              <Box key={group.date} sx={{ mb: 4 }}>
                {/* === Group Header === */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#f2f4f7',
                  px: 3,
                  py: 1.8,
                  borderRadius: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#2c3e50',
                    letterSpacing: '0.5px',
                  }}
                >
                  {dateLabel}
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#304ffe',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 24 }} />
                  {formatDuration(group.total).replace(':', 'h ') + 'm'}
                </Typography>
                  </Box>

                {/* === Table of Entries === */}
                <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: '#e9ebf0',
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, textAlign: 'center', width: 90 }}
                        >
                          Billable
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, textAlign: 'center', width: 100 }}
                        >
                          Mileage
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, textAlign: 'center', width: 140 }}
                        >
                          Start - End
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, textAlign: 'center', width: 100 }}
                        >
                          Hours
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, textAlign: 'center', width: 100 }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {group.entries.map((entry) => (
                        <TableRow
                          key={entry.timedata_id_pk}
                          hover
                          sx={{
                            '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          }}
                        >
                          <TableCell>{entry.client || '-'}</TableCell>
                          <TableCell
                            sx={{
                              whiteSpace: 'pre-line',
                              wordBreak: 'break-word',
                            }}
                          >
                            {entry.description || '-'}
                          </TableCell>
                          <TableCell align="center">
                            {entry.billable === 'Y' ? 'Y' : 'N'}
                          </TableCell>
                          <TableCell align="center">
                            {entry.mileage > 0 ? entry.mileage : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {entry.start_time && entry.end_time
                              ? `${entry.start_time} - ${entry.end_time}`
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {formatDuration(entry.duration).replace(':', 'h ') + 'm'}
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              justifyContent="center"
                              spacing={1}
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(entry)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDelete(entry.timedata_id_pk)
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </Card>

        {/* === Add Client Dialog === */}
        <Dialog open={openClientDialog} onClose={() => setOpenClientDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Client</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Client Name"
              type="text"
              fullWidth
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddClient();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenClientDialog(false)}>Cancel</Button>
            <Button onClick={handleAddClient} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Add Client'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* === Backup Data Dialog === */}
        <Dialog open={openBackupDialog} onClose={() => setOpenBackupDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BackupIcon />
              <Typography variant="h6">Backup Data</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Export all time tracking data and clients. Choose your preferred format below.
            </Typography>

            <Stack spacing={2}>
              <Button
                variant="outlined"
                size="large"
                startIcon={backupLoading ? <CircularProgress size={20} /> : <BackupIcon />}
                onClick={() => handleBackupData('json')}
                disabled={backupLoading}
                sx={{ justifyContent: 'flex-start', py: 2 }}
              >
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    JSON Format
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Complete backup with all data and metadata
                  </Typography>
                </Box>
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={backupLoading ? <CircularProgress size={20} /> : <BackupIcon />}
                onClick={() => handleBackupData('csv')}
                disabled={backupLoading}
                sx={{ justifyContent: 'flex-start', py: 2 }}
              >
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    CSV Format
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Time entries only, compatible with Excel
                  </Typography>
                </Box>
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenBackupDialog(false)} disabled={backupLoading}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* === Client Report Dialog === */}
        <Dialog
          open={openClientReportDialog}
          onClose={() => {
            setOpenClientReportDialog(false);
            setReportEntries([]);
            setReportClientId('');
            setReportStartDate('');
            setReportEndDate('');
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SearchIcon />
              <Typography variant="h6">Client Reports</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View all work done for a specific client within a chosen time frame.
            </Typography>

            {/* Filters */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Select Client
                </Typography>
                <Select
                  value={reportClientId}
                  onChange={(e) => setReportClientId(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">Choose a client</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Start Date
                  </Typography>
                  <TextField
                    type="date"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    End Date
                  </Typography>
                  <TextField
                    type="date"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Stack>

              <Button
                variant="contained"
                onClick={handleGenerateClientReport}
                disabled={reportLoading}
                startIcon={reportLoading ? <CircularProgress size={20} /> : <SearchIcon />}
              >
                Generate Report
              </Button>
            </Stack>

            {/* Results */}
            {reportEntries.length > 0 && (
              <Box>
                {/* Summary Statistics */}
                <Card sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Stack direction="row" spacing={3} justifyContent="space-around">
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#304ffe' }}>
                        {reportEntries.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Entries
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#00897b' }}>
                        {formatDuration(reportEntries.reduce((sum, e) => sum + (parseFloat(e.duration) || 0), 0)).replace(':', 'h ') + 'm'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Hours
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                        {formatDuration(reportEntries.filter(e => e.billable === 'Y').reduce((sum, e) => sum + (parseFloat(e.duration) || 0), 0)).replace(':', 'h ') + 'm'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Billable Hours
                      </Typography>
                    </Box>
                  </Stack>
                </Card>

                {/* Entries Table */}
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#e9ebf0' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Billable</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportEntries.map((entry) => {
                        const entryDate = parseEntryDate(entry);
                        const dateLabel = entryDate
                          ? entryDate.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '-';

                        return (
                          <TableRow
                            key={entry.timedata_id_pk}
                            hover
                            sx={{
                              '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                            }}
                          >
                            <TableCell>{dateLabel}</TableCell>
                            <TableCell>{entry.user_}</TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                              {entry.description || '-'}
                            </TableCell>
                            <TableCell align="center">
                              {entry.start_time && entry.end_time
                                ? `${entry.start_time} - ${entry.end_time}`
                                : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {formatDuration(entry.duration).replace(':', 'h ') + 'm'}
                            </TableCell>
                            <TableCell align="center">
                              {entry.billable === 'Y' ? 'Yes' : 'No'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  variant="outlined"
                  onClick={handleExportClientReport}
                  startIcon={<AssessmentIcon />}
                  sx={{ mt: 2 }}
                >
                  Export to CSV
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenClientReportDialog(false);
                setReportEntries([]);
                setReportClientId('');
                setReportStartDate('');
                setReportEndDate('');
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default TimeTracking;